import React, { useEffect, useState } from "react";
import { apiGet } from "../../../services/api";
import "./SupplierLedger.css";

interface Supplier {
  id: number;
  name: string;
}

interface LedgerRow {
  date?: string;
  type?: string;
  ref?: string;
  receipt_no?: string;
  debit?: string | number;
  credit?: string | number;
  balance?: number;
}

interface PurchaseBill {
  id: number;
  invoice_no?: string;
  date?: string;
  total?: number;
  paid?: number;
  balance?: number;
}

interface SupplierLedgerProps {
  supplier: Supplier | null;
}

export default function SupplierLedger({ supplier }: SupplierLedgerProps) {
  const [rows, setRows] = useState<LedgerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ billed: 0, paid: 0, pending: 0 });

  useEffect(() => {
    if (supplier?.id) {
      fetchData();
    } else {
      setRows([]);
      setStats({ billed: 0, paid: 0, pending: 0 });
    }
  }, [supplier]);

  const fetchData = async () => {
    if (!supplier?.id) return;
    setLoading(true);
    try {
      const [ledgerRes, billsRes] = await Promise.all([
        apiGet(`/api/manager/supplier/${supplier.id}/ledger`),
        apiGet(`/api/manager/supplier/${supplier.id}/bills`)
      ]);

      const ledger = Array.isArray(ledgerRes) ? (ledgerRes as LedgerRow[]) : [];
      const billList = Array.isArray(billsRes) ? (billsRes as PurchaseBill[]) : [];

      setRows(ledger);

      // Compute stats from bills
      const totalBilled = billList.reduce((sum, b) => sum + Number(b.total || 0), 0);
      const totalPaid = billList.reduce((sum, b) => sum + Number(b.paid || 0), 0);
      setStats({
        billed: totalBilled,
        paid: totalPaid,
        pending: totalBilled - totalPaid
      });

    } catch (e) {
      console.error("Failed to load supplier data", e);
    } finally {
      setLoading(false);
    }
  };

  if (!supplier) {
    return (
      <div className="supplier-ledger-empty">
        👈 Select a supplier to view details
      </div>
    );
  }

  return (
    <div className="supplier-ledger-card">
      <div className="sl-header">
        <h4>🏭 {supplier.name}</h4>
        <div className="sl-stats">
          <div className="stat-pill">Billed: ₹{stats.billed.toLocaleString("en-IN")}</div>
          <div className="stat-pill">Paid: ₹{stats.paid.toLocaleString("en-IN")}</div>
          <div className="stat-pill due">Due: ₹{stats.pending.toLocaleString("en-IN")}</div>
        </div>
      </div>

      <div className="sl-content">
        {loading ? <p className="muted">Loading...</p> : (
          <div className="table-wrapper">
            <table className="ledger-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Bill#</th>
                  <th>Receipt#</th>
                  <th className="text-right">Debit</th>
                  <th className="text-right">Credit</th>
                  <th className="text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} className="text-center muted">No transactions found</td></tr>
                ) : (
                  rows.map((r, i) => (
                    <tr key={i}>
                      <td>{r.date}</td>
                      <td>{r.type}</td>
                      <td>{r.ref}</td>
                      <td>{r.receipt_no || "-"}</td>
                      <td className="text-right debit">{r.debit ? `₹${r.debit}` : "-"}</td>
                      <td className="text-right credit">{r.credit ? `₹${r.credit}` : "-"}</td>
                      <td className={`text-right ${Number(r.balance || 0) > 0 ? "due" : "paid"}`}>
                        ₹{r.balance}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
